import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '$ui/Flo/Icon';
import { useEnvironment } from '@/config/react';

interface Props {
    itemHref: string;
    itemImg: string;
    itemDescription: string;
    itemLearnMore?: string;
}

const getUrl = (app: string): string => {
    const env = useEnvironment();

    if (env === undefined) {
        throw new Error(
            'Environment must be set at this point. This ' +
                'is a serious programming error and the application is ' +
                'in an invalid state.',
        );
    }

    const urls = {
        development: {
            icomply: 'https://icomply.agiliosoftware.io',
            iteam: 'https://iteam-staging.agiliosoftware.com',
            iservice: 'https://iservice-staging.agiliosoftware.io',
            ilearn: 'https://dental-ilearn.clarity-connect.co.uk',
            iplan: 'https://iplan.agiliosoftware.io/',
            ishop: 'https://agilio-dental-shop.ohso-staging.co.uk',
        },
        staging: {
            icomply: 'https://icomply.agiliosoftware.io',
            iteam: 'https://iteam-staging.agiliosoftware.com',
            iservice: 'https://iservice-staging.agiliosoftware.io',
            ilearn: 'https://dental-ilearn.clarity-connect.co.uk',
            iplan: 'https://iplan.agiliosoftware.io/',
            ishop: 'https://agilio-dental-shop.ohso-staging.co.uk',
        },
        production: {
            icomply: 'https://icomply.agiliosoftware.com/',
            iteam: 'https://iteam.agiliosoftware.com/',
            iservice: 'https://iservice.agiliosoftware.com/',
            ilearn: 'https://dental-ilearn.agiliosoftware.com/',
            iplan: 'https://iplan.agiliosoftware.com/',
            ishop: 'https://shop.agiliosoftware.com/',
        },
    };

    return urls[env.toLowerCase()][app];
};

const getItems = () => {
    return [
        {
            id: 0,
            href: getUrl('icomply'),
            img: 'https://content.agiliosoftware.com/content/agilio/icomply/logos/icomply-simple.png',
            description: 'Compliance software and support for CQC success',
            learnMore: 'https://agiliosoftware.com/dental/icomply/',
        },
        {
            id: 1,
            href: getUrl('iteam'),
            img: 'https://content.agiliosoftware.com/content/agilio/iteam/logos/iteam-simple.png',
            description: 'Your virtual HR department',
            learnMore: 'https://agiliosoftware.com/dental/iteam/',
        },
        {
            id: 2,
            href: getUrl('iservice'),
            img: 'https://content.agiliosoftware.com/content/agilio/iservice/logos/iservice-simple.png',
            description: 'Manage your dental assets with ease',
            learnMore: 'https://agiliosoftware.com/dental/iservice/',
        },
        {
            id: 3,
            href: getUrl('ilearn'),
            img: 'https://content.agiliosoftware.com/content/agilio/ilearn/logos/ilearn-simple.png',
            description: 'UK’s largest healthcare e-learning platform',
            learnMore: 'https://agiliosoftware.com/ilearn/ilearn-dental/',
        },
        {
            id: 4,
            href: getUrl('iplan'),
            img: 'https://content.agiliosoftware.com/content/agilio/iplan/logos/iplan-simple.png',
            description: 'Dental plans with a difference',
            learnMore: 'https://agiliosoftware.com/dental/iplan/',
        },
        {
            id: 5,
            href: getUrl('ishop'),
            img: 'https://content.agiliosoftware.com/content/dental/shop/logos/dental-shop.png',
            description: 'For you compliance logbooks and consumables',
            learnMore: undefined,
        },
    ];
};

const AppSwitcherHeader = () => {
    const imageSrc =
        'https://content.agiliosoftware.com/content/agilio/icons/agilio-a.svg';
    const headerTitle = 'Agilio Apps';

    return (
        <Header>
            <Head>
                <img src={imageSrc} alt="Header Image" />
                <h3>{headerTitle}</h3>
            </Head>
        </Header>
    );
};

const AppSwitcherItem = (props: Props) => {
    const { itemHref, itemImg, itemDescription, itemLearnMore } = props;

    const [hovering, setHovering] = React.useState(false);

    const LearnMoreWrapper = () => {
        return (
            <a href={itemLearnMore} target="_blank" rel="noreferrer">
                <LearnMore>
                    <Icon icon="QuestionMark" size={2.25} />
                </LearnMore>
            </a>
        );
    };

    return (
        <Item
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <a href={itemHref} target="_blank" rel="noreferrer">
                <Link>
                    <img src={itemImg} alt="Item Image" />
                    <p>{itemDescription}</p>
                </Link>
            </a>
            {hovering && itemLearnMore ? <LearnMoreWrapper /> : null}
        </Item>
    );
};

const AppSwitcherList = () => {
    const items = getItems();
    const listItems = items.map((item) => (
        <AppSwitcherItem
            key={item.id}
            itemHref={item.href}
            itemImg={item.img}
            itemDescription={item.description}
            itemLearnMore={item.learnMore}
        />
    ));

    return <List role="list">{listItems}</List>;
};

export function AppSwitcherMenu() {
    return (
        <Container>
            <AppSwitcherHeader />
            <AppSwitcherList />
        </Container>
    );
}

const Container = styled.div`
    color: grey;
    width: 600px;
    position: absolute;
    z-index: 3;
    background-color: #fff;
    border: 1px solid #d3d3d3;
    overflow: hidden;
    margin-top: 0.4em;
    display: flex;
    flex-direction: column;
    top: 48px;
    border-radius: 4px;
`;

const Header = styled.div`
    border-width: 0;
    border-style: solid;
    border-color: inherit;
    border-bottom-width: 1px;
`;

const Head = styled.div`
    position: relative;
    padding: 1rem;
    align-items: center;
    display: flex;

    img {
        width: 40px;
        object-fit: cover;
    }

    h3 {
        font-weight: bold;
        font-size: 1.2em;
        font-family: 'DM Sans', sans-serif;
        color: #6c757d;
        display: inline;
        margin-bottom: 0;
        margin-left: 1rem;
        margin-top: 0;
    }
`;

const List = styled.div`
    padding: 1rem;
`;

const Item = styled.div`
    padding: 1em;

    a {
        text-decoration: none;
        color: #212529;
    }

    :hover {
        background-color: #f0f0f0;
        cursor: pointer;
    }
`;

const Link = styled.div`
    img {
        height: 30px;
        object-fit: contain;
        margin-bottom: 0.5em;
    }

    p {
        font-size: 16px;
        margin: 0;
    }

    :hover {
    }
`;

const LearnMore = styled.div`
    position: absolute;
    right: 20px;
    transform: translateY(-160%);

    :hover::before {
        content: 'Learn more';
        border-radius: 0.25em;
        background: #484e61;
        padding: 0.25em 0.5em;
        color: #fff;
        margin-right: 0.5em;
        font-size: 12px;
        bottom: 4px;
        left: -83px;
        position: absolute;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto,
            'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif,
            'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
            'Noto Color Emoji';
    }
`;
